'use client';

import { useCallback, useId, useRef, useState } from 'react';

import type { AddressSuggestion } from '@/lib/address';
import { useAddressAutocomplete } from '@/lib/hooks/use-address-autocomplete';
import { opendoorUrl } from '@/lib/urls';

export interface AddressAutocompleteProps {
  placeholder?: string;
  size?: 'default' | 'small';
  initialValue?: string;
  onSelect?: (suggestion: AddressSuggestion, sessionToken: string) => void;
  // Optional CTA rendered as a footer item INSIDE the dropdown listbox.
  // Mirrors cosmos's `customAction` slot in cinderblocks AutocompleteInput,
  // which renders the "Enter address manually instead" link as part of the
  // dropdown chrome (NOT between the input and the dropdown — doing the
  // latter extends the relative parent's height and pushes the absolutely
  // positioned listbox below the CTA, producing a visual "duplicate address
  // bar" stack: [input] [CTA] [single-suggestion dropdown that looks like a
  // second input]).
  //
  // Visible only while the input is focused and the user has typed something
  // but Google/Casa returned few (<5) predictions, mirroring cosmos's
  // ManualEntryButton visibility rule. The dropdown is opened to host the CTA
  // even when there are zero suggestions to surface, so the user always has a
  // manual-entry escape hatch on a focused field with typed text.
  belowAction?: React.ReactNode;
}

function defaultNavigate(suggestion: AddressSuggestion) {
  const params = new URLSearchParams({ address: suggestion.description });
  if (suggestion.placeId) params.set('placeId', suggestion.placeId);
  if (suggestion.addressToken) params.set('addressToken', suggestion.addressToken);
  window.location.href = `${opendoorUrl('/sell')}?${params.toString()}`;
}

interface SuggestionRowProps {
  id: string;
  suggestion: AddressSuggestion;
  index: number;
  isHighlighted: boolean;
  onSelect: (suggestion: AddressSuggestion) => void;
  onHover: (index: number) => void;
}

function preventDefault(e: React.MouseEvent) {
  e.preventDefault();
}

function SuggestionRow({
  id,
  suggestion,
  index,
  isHighlighted,
  onSelect,
  onHover,
}: SuggestionRowProps) {
  const handleClick = useCallback(() => onSelect(suggestion), [onSelect, suggestion]);
  const handleHover = useCallback(() => onHover(index), [onHover, index]);
  return (
    <li
      id={id}
      role="option"
      aria-selected={isHighlighted}
      tabIndex={-1}
      onMouseDown={preventDefault}
      onClick={handleClick}
      onMouseEnter={handleHover}
      className={`cursor-pointer px-5 py-4 text-ink transition text-body-md ${
        isHighlighted ? 'bg-state-hover' : ''
      }`}
    >
      {suggestion.description}
    </li>
  );
}

export function AddressAutocomplete({
  placeholder = 'Enter your home address',
  size = 'default',
  initialValue = '',
  onSelect,
  belowAction,
}: AddressAutocompleteProps) {
  const inputId = useId();
  const listboxId = useId();
  const [value, setValue] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const { suggestions, isLoading, error, sessionToken } = useAddressAutocomplete(value);

  const inputPad = size === 'small' ? 'px-4 py-3' : 'px-5 py-4';

  // Mirror cosmos's manual-entry-CTA visibility rule: only show when the user
  // is actively typing into a focused input and the autocomplete returned
  // fewer than 5 predictions (i.e., the address probably isn't going to
  // appear). Avoids surfacing the link when the user already has good
  // suggestions or when the field is dormant.
  const showBelowAction = open && value.trim() !== '' && suggestions.length < 5;

  const handleSelect = useCallback(
    (suggestion: AddressSuggestion) => {
      setValue(suggestion.description);
      setOpen(false);
      setHighlighted(-1);
      if (onSelect) {
        onSelect(suggestion, sessionToken);
      } else {
        defaultNavigate(suggestion);
      }
    },
    [onSelect, sessionToken],
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setOpen(true);
    setHighlighted(-1);
  }, []);

  const handleFocus = useCallback(() => setOpen(true), []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const next = e.relatedTarget;
    if (next && containerRef.current?.contains(next)) return;
    setTimeout(() => setOpen(false), 100);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        setOpen(true);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlighted((i) => Math.min(i + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlighted((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && highlighted >= 0 && suggestions[highlighted]) {
        e.preventDefault();
        handleSelect(suggestions[highlighted]);
      } else if (e.key === 'Escape') {
        setOpen(false);
        setHighlighted(-1);
      }
    },
    [open, highlighted, suggestions, handleSelect],
  );

  // The listbox opens whenever there's something to show: suggestions, an
  // error message, or the manual-entry CTA when it's enabled. The CTA-only
  // case matters for queries that return zero predictions — the dropdown is
  // otherwise empty, but we still want the manual-entry escape hatch visible.
  const hasBelowAction = Boolean(belowAction) && showBelowAction;
  const showList =
    open && (suggestions.length > 0 || (error !== null && value.trim() !== '') || hasBelowAction);
  const optionId = (index: number) => `${listboxId}-option-${index}`;
  const activeOptionId =
    highlighted >= 0 && suggestions[highlighted] ? optionId(highlighted) : undefined;

  return (
    <div
      ref={containerRef}
      role="combobox"
      aria-expanded={showList}
      aria-haspopup="listbox"
      aria-controls={listboxId}
      className="relative"
    >
      <div className="relative">
        <input
          id={inputId}
          type="text"
          autoComplete="off"
          spellCheck={false}
          aria-label={placeholder}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          aria-busy={isLoading}
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`w-full rounded-2xl border border-line-strong bg-canvas text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-accent ${inputPad} pr-12 text-body-md`}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-ink-faint"
        >
          {isLoading ? (
            <svg width="20" height="20" viewBox="0 0 20 20" className="animate-spin">
              <circle
                cx="10"
                cy="10"
                r="7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="20 30"
                fill="none"
              />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M14 14L17 17"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          )}
        </span>
      </div>

      {/* Always rendered so the input's aria-controls reference always resolves;
          hidden (not unmounted) while collapsed. */}
      <ul
        id={listboxId}
        role="listbox"
        hidden={!showList}
        className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-accent bg-canvas shadow-lg"
      >
        {showList && (
          <>
            {suggestions.length === 0 && error ? (
              <li className="px-5 py-4 text-ink-muted text-body-md">
                Could not load suggestions. Try again.
              </li>
            ) : (
              suggestions.map((suggestion, i) => {
                const key = `${suggestion.source}-${suggestion.placeId ?? suggestion.addressToken ?? suggestion.description}`;
                return (
                  <SuggestionRow
                    key={key}
                    id={optionId(i)}
                    suggestion={suggestion}
                    index={i}
                    isHighlighted={i === highlighted}
                    onSelect={handleSelect}
                    onHover={setHighlighted}
                  />
                );
              })
            )}
            {hasBelowAction ? (
              <li
                className={`px-5 py-4 ${
                  suggestions.length > 0 || error ? 'border-t border-line-soft' : ''
                }`}
              >
                {belowAction}
              </li>
            ) : null}
          </>
        )}
      </ul>
    </div>
  );
}
